import { useEffect, useState } from 'react';
import { cn } from '../../utils/cn';
import { formatRelative, formatDateTime } from '../../utils/formatters';

/**
 * <RelativeTime/> — primitivo del DS para mostrar tiempo relativo con la
 * fuente única `formatRelative`. Renderiza un <span> cuyo `title` es la fecha
 * absoluta (tooltip nativo). Hereda `currentColor`, por lo que es dark-safe sin
 * forzar ningún color; usa `tabular-nums` para que el ancho no salte al tickear.
 *
 * @param {object} props
 * @param {number|string|Date} props.value  epoch ms | ISO string | Date.
 * @param {string|false} [props.tooltip]  texto del `title`. Default:
 *   `formatDateTime(value)`. Pasar `false` para no renderizar tooltip.
 * @param {boolean} [props.live=false]  re-renderiza cada ~45 s para mantener el
 *   relativo fresco (estado interno, no toca datos).
 * @param {(key:string, vars?:object)=>string} [props.t]  i18n para el relativo.
 * @param {object} [props.options]  opciones extra reenviadas a `formatRelative`
 *   (p.ej. `{ seconds: true, dayWords: false }`).
 * @param {string} [props.className]
 */
export function RelativeTime({
  value,
  tooltip,
  live = false,
  t,
  options,
  className,
  ...rest
}) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => setTick((n) => (n + 1) % 1_000_000), 45_000);
    return () => clearInterval(id);
  }, [live]);

  if (value == null || value === '') return null;

  const relOpts = { ...(options || {}), ...(t ? { t } : {}) };
  const relative = formatRelative(value, relOpts);
  if (!relative) return null;

  const title = tooltip === false ? undefined : (tooltip ?? formatDateTime(value));

  return (
    <span {...rest} title={title} className={cn('tabular-nums', className)}>
      {relative}
    </span>
  );
}
